import React, { useRef } from 'react';
import { Flex } from '@mantine/core';
import ScDateControl from '@/components/sc-controls/form-controls/v2/ScDateControl';
import SCTimeControl from '@/components/sc-controls/form-controls/v2/SCTimeControl';
import Time from '@/utils/time';
import { FieldProps } from '../form-helpers';

export type FormFieldDateTimeProps = Omit<FieldProps, 'value' | 'onChange'> & {
    value: Date | null;
    onChange: (value: Date | null) => void;
};

// Unlike `ScDateControl` (which takes/returns a plain `Date`, mirroring Mantine's own
// `DateInput`), `SCTimeControl` does NOT work on a full `Date`. Its `value` prop is typed as a
// `yyyy-mm-ddTHH:MM:SS` ISO string (see `ScTimeControlCustomProps.value` in
// `components/sc-controls/form-controls/v2/SCTimeControl.tsx`), and its `onChange` fires with
// either a full `Date` (when a dropdown option is picked, via `new Date(isoStringDate)`) or a
// partial ISO string with only the time portion changed (when typed manually, via
// `isoDateSplit[0] + 'T' + time`). Neither shape can be passed straight through to our
// `Date | null`-typed onChange, so we merge just the *time* portion onto the field's existing
// date — see `handleTimeChange` below for why that merge is done with a regex rather than
// `Time.updateTime` (the legacy `handleDateTimeFormItemChangeSC` in
// `components/modals/form/manage-form.tsx:395-408` used the latter, but it can't tolerate a
// date-less partial string).
// Note: `SCTimeControl`'s `format` prop is typed as `'12' | '24'` (12/24-hour clock), not a
// date-format string like the legacy `SCTimePicker`'s `format="HH:mm:ss"` — passing that string
// literal fails typecheck. Seconds granularity is controlled separately via `withSeconds`.
function FormFieldDateTime({ label, required, value, onChange, onBlur, error, disabled, inTable }: FormFieldDateTimeProps) {
    // Only used to give handleTimeChange a date to merge the picked/typed time onto when the field
    // is still empty (e.g. the user opens the time dropdown before picking a date) - NOT used for
    // display, otherwise the time input would show today's current time on a genuinely empty field.
    const baseDate = value ?? Time.now();

    // `SCTimeControl` reconstructs its own "changed" value by splitting *our* `value` prop on
    // "T"/space and re-appending the newly picked/typed time (see its `handleTimeInputChange`).
    // Since the field passes `''` when empty (so the time input doesn't show today's current time
    // as a false default), that split yields a date-less string like "T14:30:00" whenever the very
    // first pick happens on an empty field. `Time.updateTime`/`parseDate` can't parse that (no date
    // portion), which corrupts the merged Date into an Invalid Date via `setHours(NaN)` — and that
    // then crashes downstream (`Date.toISOString` inside Mantine's DateInput) once it's rendered
    // back into `ScDateControl`. So the hours/minutes/seconds are pulled out with a regex instead of
    // going through `Time.updateTime`'s string parsing, which works whether or not a date portion is
    // present, and `baseDate` is never trusted to have been mutated in place.
    const mergeTime = (newTime: unknown): Date | null => {
        let hours: number;
        let minutes: number;
        let seconds: number;
        if (newTime instanceof Date && !Number.isNaN(newTime.getTime())) {
            hours = newTime.getHours();
            minutes = newTime.getMinutes();
            seconds = newTime.getSeconds();
        } else {
            const match = String(newTime ?? '').match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
            if (!match) return null;
            hours = Number(match[1]);
            minutes = Number(match[2]);
            seconds = Number(match[3] ?? 0);
        }
        const updated = new Date(baseDate);
        updated.setHours(hours, minutes, seconds, 0);
        return updated;
    };

    // A keystroke in `SCTimeControl`'s native time input fires on every segment change, including
    // ambiguous ones (a lone "2" typed for the hour, which could still become 20-23). Calling
    // `onChange` straight from here would round-trip back down as `SCTimeControl`'s own `value`
    // prop (`Time.toISOString` below), which resets its internal `timeStringValue` on every change
    // — and a native `<input type="time">` drops its pending-digit buffer the instant its `value`
    // is reassigned mid-edit, so the second "2" of "22" is silently lost and has to be retyped. The
    // legacy editor (`components/modals/form/manage-form.tsx`) never hits this because it renders
    // this same `SCTimeControl` through a plain, unbatched state update rather than this field's
    // per-keystroke-controlled wrapper.
    //
    // A dropdown pick (`newTime instanceof Date`) is a discrete, complete action rather than typed
    // keystrokes, so it still commits immediately, same as before.
    const pendingTimeRef = useRef<Date | null>(null);

    const handleTimeChange = (newTime: unknown) => {
        if (newTime instanceof Date && !Number.isNaN(newTime.getTime())) {
            pendingTimeRef.current = null;
            const updated = mergeTime(newTime);
            if (updated) onChange(updated);
            return;
        }
        const updated = mergeTime(newTime);
        if (updated) pendingTimeRef.current = updated;
    };

    const handleTimeBlur = () => {
        if (pendingTimeRef.current) {
            onChange(pendingTimeRef.current);
            pendingTimeRef.current = null;
        }
        onBlur?.();
    };

    return (
        <Flex gap="sm" align="flex-end" w={inTable ? '100%' : undefined}>
            {/* Mantine's DateInput shows its clear button from `clearable && value && !readOnly`
                alone - it never checks `disabled` - so `clearable` itself has to be turned off
                here or a disabled DateTime field would still let you clear it via the date side. */}
            <ScDateControl label={label} required={required} value={value} onChange={(val: Date | null) => onChange(val)} onBlur={onBlur} error={error} disabled={disabled} maw={inTable ? '100%' : undefined} flex={inTable ? 1 : undefined} clearable={!disabled} />
            <SCTimeControl
                required={required}
                value={value ? Time.toISOString(baseDate, false, true) : ''}
                onChange={handleTimeChange}
                onBlur={handleTimeBlur}
                error={error}
                disabled={disabled}
                withSeconds
                maw={inTable ? '100%' : undefined}
                flex={inTable ? 1 : undefined}
            />
        </Flex>
    );
}

export default React.memo(FormFieldDateTime);
